import React, { useEffect, useMemo, useState } from "react";
import { Box, TextField, MenuItem, Autocomplete, CircularProgress, Typography } from "@mui/material";
import { TEXT_SECONDARY } from "../../../theme/tokens";
import {
  searchWeatherLocations, WeatherLocation,
} from "../../Player/weather";

/**
 * Weather zone settings: a searchable location selector (Open-Meteo geocoding)
 * that stores a STABLE location — latitude/longitude/display name/timezone —
 * rather than a fuzzy string, plus the temperature unit. The shared
 * WeatherWidget renders live data from these coordinates.
 */
const WeatherInspector: React.FC<{
  cfg: Record<string, any>;
  setCfg: (patch: Record<string, any>) => void;
}> = ({ cfg, setCfg }) => {
  const [input, setInput] = useState("");
  const [options, setOptions] = useState<WeatherLocation[]>([]);
  const [loading, setLoading] = useState(false);

  // The saved location, shown as the current Autocomplete value.
  const selected: WeatherLocation | null = useMemo(() => {
    if (typeof cfg.latitude === "number" && typeof cfg.longitude === "number") {
      return {
        name: cfg.location_name ?? "",
        latitude: cfg.latitude,
        longitude: cfg.longitude,
        timezone: cfg.timezone ?? null,
        display_name: cfg.location_name ?? `${cfg.latitude}, ${cfg.longitude}`,
      };
    }
    return null;
  }, [cfg.latitude, cfg.longitude, cfg.location_name, cfg.timezone]);

  // Debounced geocoding search.
  useEffect(() => {
    const q = input.trim();
    if (q.length < 2) { setOptions([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      const res = await searchWeatherLocations(q);
      if (!cancelled) { setOptions(res); setLoading(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [input]);

  return (
    <>
      <Autocomplete<WeatherLocation>
        size="small"
        options={options}
        loading={loading}
        value={selected}
        // We drive filtering server-side; don't also filter locally.
        filterOptions={(x) => x}
        isOptionEqualToValue={(a, b) => a.latitude === b.latitude && a.longitude === b.longitude}
        getOptionLabel={(o) => (typeof o === "string" ? o : o.display_name)}
        onInputChange={(_, v) => setInput(v)}
        onChange={(_, v) => {
          if (v && typeof v !== "string") {
            setCfg({
              latitude: v.latitude,
              longitude: v.longitude,
              location_name: v.display_name,
              timezone: v.timezone ?? null,
            });
          } else if (v === null) {
            setCfg({ latitude: null, longitude: null, location_name: "", timezone: null });
          }
        }}
        renderInput={(params) => (
          <TextField {...params} label="Location"
            placeholder="Search a city, e.g. Oshawa"
            helperText="Type to search. Live weather uses the selected location's coordinates."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ mb: 1 }}
      />

      {!selected && (
        <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mb: 1 }}>
          Select a location to show live weather.
        </Typography>
      )}

      <TextField select size="small" fullWidth label="Temperature unit"
        value={cfg.units ?? "metric"}
        onChange={(e) => setCfg({ units: e.target.value })}
        sx={{ mb: 1 }}
      >
        <MenuItem value="metric">Celsius (°C)</MenuItem>
        <MenuItem value="imperial">Fahrenheit (°F)</MenuItem>
      </TextField>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
        <TextField
          size="small" type="number" label="Font size (%)"
          inputProps={{ min: 10, max: 300, step: 5 }}
          value={cfg.font_scale ?? 100}
          onChange={(e) => setCfg({ font_scale: Number(e.target.value) })}
        />
        <TextField
          size="small" type="color" label="Color"
          value={cfg.color ?? "#FFFFFF"}
          onChange={(e) => setCfg({ color: e.target.value })}
        />
      </Box>
      <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.5 }}>
        Scales the auto-fitted text. 100 = default.
      </Typography>
      <Box sx={{ mt: 0.5 }} />
    </>
  );
};

export default WeatherInspector;
